// Updated services data with clearer, benefit‑focused copy
export const SERVICES = [
  {
    id: 'forecasting-modelling',
    shortTitle: 'Forecasting & Modelling',
    title: 'Forecasting & Modelling',
    headline: 'Data‑driven forecasts and scenario models to guide strategic decisions.',
    kicker: 'Overview',
    image: 'https://images.unsplash.com/photo-1494232410401-ad00d5433cfa?auto=format&fit=crop&q=80',
    summary: 'We turn raw data into reliable forecasts and quantitative models that help leaders anticipate market trends and evaluate strategic options.',
    hiddenSections: ['approach', 'deliverables', 'useCases', 'applications', 'why'],
    intro: [
      'Our forecasting service combines statistical rigor with domain expertise to deliver actionable insights.',
      'We build bespoke models—time‑series, econometric, or simulation—to predict demand, price movements, and competitive dynamics.',
      'Results are presented in clear visualisations and executive‑ready briefings.'
    ],
    questionsEyebrow: 'Capabilities',
    questionsTitle: 'What we can help you with',
    questions: [
      'Demand forecasting – accurate volume predictions for products, services, or markets.',
      'Scenario modelling – explore “what‑if” outcomes for strategic initiatives or regulatory changes.',
      'Risk quantification – translate uncertainty into probabilistic risk metrics.',
      'Pricing optimisation – data‑driven price recommendations based on elasticity and competitive data.',
      'Strategic planning – integrate forecasts into long‑term business plans and investment decisions.'
    ],
    approachEyebrow: 'Practice',
    approachTitle: 'Our methodology',
    approachIntro: 'We blend statistical techniques, machine learning, and industry knowledge to produce robust, transparent models.',
    approach: [
      'Data collection and cleaning',
      'Exploratory analysis and feature engineering',
      'Model selection and validation',
      'Scenario construction and stress testing',
      'Insightful visualisation and reporting'
    ],
    deliverablesEyebrow: 'Deliverables',
    deliverablesTitle: 'What you receive',
    deliverables: [
      'Custom forecast model with documentation',
      'Interactive dashboards for scenario exploration',
      'Executive briefing deck with key takeaways',
      'Technical appendix with methodology and validation metrics'
    ],
    useCasesEyebrow: 'Typical Situations',
    useCasesTitle: 'Where we add value',
    useCases: [
      'New product launch planning',
      'Market entry feasibility studies',
      'Regulatory impact assessment',
      'Capacity and supply chain planning',
      'Investor presentations and valuation support'
    ],
    applicationsTitle: 'Our team',
    applications: [
      {
        title: 'Keystone Economists',
        description: 'Statistical expertise to ensure methodological soundness.'
      },
      {
        title: 'Keystone Data Science',
        description: 'Machine‑learning engineers who build scalable predictive models.'
      },
      {
        title: 'Keystone Strategy',
        description: 'Translates quantitative results into strategic recommendations for leadership.'
      }
    ],
    why: 'Accurate forecasting reduces uncertainty, enabling better capital allocation and risk management in fast‑changing markets.',
    closing: 'Empower your decisions with data‑driven confidence and clear, actionable insights.'
  },
  {
    id: 'ai-upskilling-implementation',
    shortTitle: 'AI Upskilling & Implementation',
    title: 'AI Upskilling and Implementation',
    headline: 'Equip your team and operationalize AI for real impact.',
    kicker: 'Overview',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80',
    summary: 'We help organisations build AI capability through tailored training, hands‑on implementation support, and change management, turning knowledge into measurable outcomes.',
    hiddenSections: ['approach', 'deliverables', 'useCases', 'applications', 'formats', 'why'],
    intro: [
      'Our AI upskilling service blends expert curriculum design with practical implementation labs to accelerate capability.',
      'We work side‑by‑side with your team, delivering both the knowledge and the tooling needed to deploy AI solutions at scale.',
      'From foundational data skills to production‑grade model deployment, we ensure lasting impact across the organization.'
    ],
    questionsEyebrow: 'What We Offer',
    questionsTitle: 'Core capabilities',
    questions: [
      'Custom AI curriculum development aligned to business objectives.',
      'Interactive workshops and hands‑on labs covering data pipelines, model training, and evaluation.',
      'MLOps enablement and production deployment guidance.',
      'Data engineering foundations for reliable AI pipelines.',
      'Change management and adoption strategies.',
      'AI ethics and responsible AI training.',
      'Executive briefings and AI roadmaps.',
      'Ongoing mentorship and support post‑implementation.'
    ],
    approachIntro: 'Our approach combines skill‑gap assessment, curriculum design, and real‑world implementation to embed AI competence throughout your organization.',
    approach: [
      'Assess current AI skill levels and business needs.',
      'Design a bespoke training program mapped to strategic goals.',
      'Deliver interactive workshops and lab sessions.',
      'Build end‑to‑end AI pipelines in your environment.',
      'Establish MLOps processes and monitoring.',
      'Provide governance and ethical AI guidelines.',
      'Offer post‑project coaching and continuous learning.'
    ],
    deliverables: [
      'Tailored training curriculum and materials.',
      'Workshop agendas, hands‑on lab scripts, and recordings.',
      'Production‑ready prototype model.',
      'MLOps pipeline toolkit and documentation.',
      'Adoption playbook and change management plan.',
      'Responsible AI governance framework.',
      'Executive summary and future roadmap.',
      'Support schedule for ongoing mentorship.'
    ],
    useCases: [
      'Tech teams scaling AI capabilities.',
      'Business units launching pilot AI projects.',
      'Enterprises formalising AI governance and processes.',
      'Start‑ups building MVPs with rapid upskilling.',
      'Consultancies upskilling client teams.',
      'Healthcare organisations ensuring compliant AI deployment.',
      'Financial services adopting predictive analytics responsibly.'
    ],
    applicationsTitle: 'Example engagements',
    applications: [
      {
        title: 'AI Skills Assessment',
        description: 'Evaluate team capabilities and define a roadmap for upskilling.'
      },
      {
        title: 'Hands‑on Model Deployment Workshop',
        description: 'Guided session to move a prototype to production with MLOps best practices.'
      },
      {
        title: 'End‑to‑end AI Project Coaching',
        description: 'From data preparation to model monitoring, we mentor your team through the full lifecycle.'
      },
      {
        title: 'Custom Curriculum Development',
        description: 'Design and deliver a training program tailored to your strategic objectives.'
      }
    ],
    formats: [
      {
        title: 'Full‑Day Training Sprint',
        description: 'Intensive, hands‑on session covering AI fundamentals to deployment.'
      },
      {
        title: 'Quarter‑Long Upskilling Programme',
        description: 'Series of workshops, labs, and coaching over three months.'
      },
      {
        title: 'Implementation Advisory',
        description: 'Strategic guidance paired with technical execution support.'
      }
    ],
    why: 'We combine deep technical expertise with proven instructional design to rapidly elevate AI proficiency and embed sustainable implementation practices.',
    closing: 'Turn AI knowledge into operational results, reduce time‑to‑value, and build lasting capability.'
  },
  {
    id: 'information-strategy-risk-regulation',
    shortTitle: 'Information Strategy, Risk & Regulation',
    title: 'Information Strategy, Risk & Regulation (ISRR)',
    headline: 'AI, machine learning and data‑privacy advisory for complex information challenges.',
    kicker: 'Overview',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80',
    summary: 'We provide world‑class advisory on AI, machine learning, and data‑privacy to help tech companies and regulators address high‑stakes information risks.',
    hiddenSections: ['approach', 'deliverables', 'useCases', 'applications', 'formats', 'why'],
    intro: [
      'Our ISRR practice blends auditing, analysis, and implementation expertise for algorithmic systems, privacy controls, and information‑risk exposure.'
    ],
    questionsEyebrow: 'Capabilities',
    questionsTitle: 'Key services',
    questions: [
      'Generative AI and ML model assessment – functionality, fairness, bias, data valuation, and training‑data curation.',
      'Algorithmic auditing – code and data analysis to evaluate impact and compliance.',
      'Sensitive data handling – secure assessment of biometric and other high‑risk user data.',
      'Cross‑vertical expertise – experience with software, cloud platforms, and related domains.'
    ],
    approachIntro: 'We combine auditing, analysis, and implementation to evaluate algorithmic systems, privacy controls, and information‑risk exposure.',
    approach: [
      'Algorithmic auditing',
      'ML and generative AI assessment',
      'Data‑privacy and cybersecurity analysis',
      'Sensitive data treatment review',
      'Membership‑inference risk analysis'
    ],
    deliverables: [
      'Model functionality and implementation review',
      'Fairness and bias measurement',
      'Training‑data curation assessment',
      'Privacy‑preserving system audit',
      'Source‑code and data analysis',
      'Consumer‑privacy impact assessment'
    ],
    useCases: [
      'Major technology firms',
      'Regulatory agencies',
      'Fortune‑50 corporations',
      'Top law firms',
      'Software and cloud platform providers',
      'Advertising and consumer‑tech companies',
      'Search, social media, OS, and app businesses'
    ],
    applicationsTitle: 'Example applications',
    applications: [
      {
        title: 'Algorithmic Auditing',
        description: 'Assess model behaviour, functionality, fairness, bias, data usage, and implementation choices.'
      },
      {
        title: 'Privacy‑Preserving Systems Review',
        description: 'Audit secure, compliant systems and evaluate membership‑inference and privacy risks.'
      },
      {
        title: 'Sensitive Data Assessment',
        description: 'Define, secure, and assess treatment of biometric and other sensitive user data.'
      }
    ],
    formats: [
      {
        title: 'ISRR Assessment',
        description: 'Focused advisory on algorithmic, privacy, cybersecurity, and regulatory risk.'
      },
      {
        title: 'Regulatory Support',
        description: 'Expert analysis and implementation help for regulators and high‑stakes tech matters.'
      }
    ],
    why: 'We apply AI, ML, data‑privacy, cybersecurity, and auditing expertise to information‑risk challenges where technical detail and regulatory impact matter.',
    closing: 'Rigorous analysis of algorithmic systems, privacy risks, and regulatory questions shaping major tech markets.'
  }
];
